import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Typography,
} from '@mui/material';
import axios from 'axios';
import { soaApi } from '@/services/api';
import { SoaDocument } from '@/types/soa';
import { FilterOption } from '@/components/utils/filters';
import { FilterAsyncSelect, Button } from '@/components/ui';
import { useClientApi } from '@/hooks/useClientApi';

interface SoaAssignDialogProps {
    document: SoaDocument | null;
    onClose: () => void;
    onAssigned: (warning?: string) => void;
}

const SoaAssignDialog: React.FC<SoaAssignDialogProps> = ({ document, onClose, onAssigned }) => {
    const { getClients } = useClientApi();
    const [client, setClient] = useState<FilterOption | null>(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const loadClientOptions = useCallback(async (inputValue: string): Promise<FilterOption[]> => {
        try {
            const response = await getClients({ search: inputValue, page_size: 500 });
            return response.results.map(c => ({
                value: c.id.toString(),
                label: `${c.client_code} - ${c.full_name || `${c.first_name} ${c.last_name}`}`,
            }));
        } catch {
            return [];
        }
    }, [getClients]);

    useEffect(() => {
        // Pre-filled with the current client so confirming with no change re-runs PAN
        // verification against Client Master — the same dialog covers both "assign"
        // and "re-verify" without a separate action.
        setClient(document?.client_master
            ? { value: String(document.client_master), label: document.client_name || document.client_code || '' }
            : null);
        setError(null);
    }, [document]);

    const handleAssign = async () => {
        if (!document || !client) return;
        setSaving(true);
        setError(null);
        try {
            const result = await soaApi.assignClient(document.id, parseInt(client.value, 10));
            onAssigned(result.warning);
            onClose();
        } catch (err) {
            const detail = axios.isAxiosError(err) ? err.response?.data?.detail : null;
            setError(detail || 'Could not assign the client. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={!!document} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{document?.client_master ? 'Reassign / Verify Client' : 'Assign Client'}</DialogTitle>
            <DialogContent>
                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                <Typography variant="body2" sx={{ mb: 1 }}>
                    File: <strong>{document?.original_filename}</strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Name in file: {document?.extracted_name || '—'}
                </Typography>

                <Alert severity="info" sx={{ mb: 2 }}>
                    {document?.client_master
                        ? "Re-checks whether the client's current PAN (on Client Master) opens "
                          + 'this PDF — useful after correcting a PAN there. Confirm the same '
                          + 'client to just re-verify, or pick a different one to reassign.'
                        : "On assignment we check that the client's PAN opens this PDF. If it "
                          + 'does not, the assignment is still saved but flagged as unverified.'}
                </Alert>

                <FilterAsyncSelect
                    label="Client"
                    value={client}
                    onChange={setClient}
                    loadOptions={loadClientOptions}
                    placeholder="Select a client…"
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>Cancel</Button>
                <Button variant="primary" onClick={handleAssign} disabled={!client || saving}>
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SoaAssignDialog;
